import { useEffect } from "react";

/** Replicates the JS tilt effect from templates/login.html on the visual panel. */
export function useTilt<T extends HTMLElement>(
  ref: React.RefObject<T>,
  opts: { strength?: number; scale?: number } = {}
) {
  const { strength = 14, scale = 1.04 } = opts;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const isLight = () => document.documentElement.getAttribute("data-theme") === "light";

    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const midX = rect.width / 2;
      const midY = rect.height / 2;
      const relX = (x - midX) / midX;
      const relY = (y - midY) / midY;
      const rotateY = relX * strength;
      const rotateX = -relY * strength;
      el.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${scale})`;
      el.style.boxShadow = isLight()
        ? "0 30px 60px -18px rgba(15, 23, 42, 0.18)"
        : "0 30px 60px -18px rgba(15, 23, 42, 0.95)";
      el.style.borderColor = isLight()
        ? "rgba(37, 99, 235, 0.45)"
        : "rgba(129, 140, 248, 0.9)";
    };

    const reset = () => {
      el.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)";
      el.style.boxShadow = isLight()
        ? "0 25px 50px -15px rgba(15, 23, 42, 0.12)"
        : "0 25px 50px -15px rgba(15, 23, 42, 0.85)";
      el.style.borderColor = isLight()
        ? "rgba(37, 99, 235, 0.2)"
        : "rgba(148, 163, 184, 0.5)";
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", reset);
    el.addEventListener("blur", reset);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", reset);
      el.removeEventListener("blur", reset);
    };
  }, [ref, strength, scale]);
}
